import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import EmailEditor from '../helpers/email-editor/EmailEditor';
import Loading from '../common/Loading';
import campaignService from '../../services/campaign.service';

function TemplateEmail() {
    const { templateId } = useParams();
    const [template, setTemplate] = useState(null);
    const [loadJson, setLoadJson] = useState(null);
    const [isLoading, setIsLoading] = useState(templateId !== 'new');
    const { register, handleSubmit, reset, setValue, watch } = useForm();
    const navigate = useNavigate();
    const saveFnRef = useRef(null);
    const skipNextLoadRef = useRef(false);
    const previewPath = watch('preview');
    const [formData, setFormData] = useState({
        subject: '',
        category: 'NEWSLETTER',
    });

    const handleSelectPreviewImage = (imagePath) => {
        setValue('preview', imagePath, { shouldDirty: true });
    };

    const handleEmailDesignSave = async () => {
        if (saveFnRef.current) {
            try {
                const { htmltemplate, template, thumbnail } = await saveFnRef.current();
                return { htmltemplate, template, thumbnail };
            } catch (err) {
                console.error('Save failed:', err);
                throw err;
            }
        }
    };

    useEffect(() => {
        if (templateId === 'new') {
            const newData = {
                id: null,
                type: 'EMAIL',
                name: '',
                preview: '',
                isActive: '1',
            };
            setTemplate(newData);
            reset(newData);
            setLoadJson(null);
            setFormData({ subject: '', category: 'NEWSLETTER' });
            setIsLoading(false);
            return;
        }

        // After first save, URL changes from "new" to the real id — keep current editor state.
        if (skipNextLoadRef.current) {
            skipNextLoadRef.current = false;
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        campaignService
            .getTemplateById(templateId)
            .then((res) => {
                const data = {
                    ...res.data,
                    isActive: res.data.isActive ? '1' : '0',
                };
                setTemplate(data);
                reset(data);
                setFormData({ 
                    subject: '', 
                    category: res.data.category || 'NEWSLETTER' 
                });

                if (res.data.jsonContent) {
                    const parsed =
                        typeof res.data.jsonContent === 'string'
                            ? JSON.parse(res.data.jsonContent)
                            : res.data.jsonContent;
                    setLoadJson(parsed);
                }
            })
            .catch((err) => {
                console.error(err);
                toast.error('Failed to load template.', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => setIsLoading(false));
    }, [templateId, reset]);

    const onSubmit = async (submitData) => {
        try {
            const result = await handleEmailDesignSave();
            if (!result) throw new Error('Could not get email design');

            const { htmltemplate, template } = result;
            const payload = {
                type: 'EMAIL',
                name: submitData.name,
                preview: submitData.preview,
                contentPath: htmltemplate,
                jsonContent: template,
                isActive: submitData.isActive === '1' || submitData.isActive === true,
                category: formData.category,
            };

            const request =
                templateId === 'new'
                    ? campaignService.addUpdateTemplate(payload)
                    : campaignService.updateTemplate(templateId, payload);

            request
                .then((res) => {
                    const saved = {
                        ...res.data,
                        isActive: res.data.isActive ? '1' : '0',
                    };
                    toast.success('Template saved.', {
                        position: toast.POSITION.BOTTOM_RIGHT,
                    });
                    reset(saved);
                    setTemplate(saved);
                    setFormData((prev) => ({
                        ...prev,
                        category: saved.category || prev.category,
                    }));

                    // Stay on this page; after create, switch URL to the saved id for future updates.
                    if (templateId === 'new' && saved.id) {
                        skipNextLoadRef.current = true;
                        navigate(`/campaign/emailtemplate/${saved.id}`, { replace: true });
                    }
                })
                .catch(() => {
                    toast.error('Error saving template.', {
                        position: toast.POSITION.BOTTOM_RIGHT,
                    });
                });
        } catch (error) {
            console.error(error);
            toast.error('Error exporting template.', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
        }
    };

    return (
        <div className="container container-xxl">
            <div className="pagetitle d-flex justify-content-between">
                <h1>Campaign Template</h1>
            </div>

            <div className="card shadow-sm mt-4">
                <div className="card-body pt-4">
                    {template && (
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="row g-3 mb-4">
                                <div className="col-md-3">
                                    <label className="form-label small text-muted">Template ID</label>
                                    <div className="form-control-plaintext fw-medium">
                                        {template?.id || 'New'}
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted">Status</label>
                                    <select className="form-select" {...register('isActive')}>
                                        <option value="1">Active</option>
                                        <option value="0">Inactive</option>
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small text-muted">Template Name</label>
                                    <input
                                        type="text"
                                        {...register('name')}
                                        placeholder="Enter template name"
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <div className="row g-3 mb-4">
                                <div className="col-md-6">
                                    <label className="form-label small text-muted">Email Subject</label>
                                    <input
                                        type="text"
                                        value={formData.subject}
                                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                        placeholder="Enter email subject line"
                                        className="form-control"
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted">Category</label>
                                    <select
                                        className="form-select"
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    >
                                        <option value="NEWSLETTER">Newsletter</option>
                                        <option value="BLAST">Blast</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted">Preview Image Path</label>
                                    <input
                                        type="text"
                                        {...register('preview')}
                                        placeholder="Pick from Media tab"
                                        className="form-control"
                                    />
                                    <div className="form-text">
                                        Open the Media tab in the editor and click the image icon on a file.
                                    </div>
                                    {previewPath ? (
                                        <div className="mt-2 border rounded bg-light p-2 text-center">
                                            <img
                                                src={previewPath}
                                                alt="Template preview"
                                                style={{ maxWidth: '100%', maxHeight: 72, objectFit: 'contain' }}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-2 pt-3 border-top">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate('/campaign')}
                                >
                                    <i className="bi bi-arrow-left me-1"></i>
                                    Back
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    <i className="bi bi-save me-1"></i>
                                    Save Template
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {isLoading ? (
                <Loading />
            ) : (
                 <EmailEditor  
                    loadjson={loadJson}
                    emailSubject={formData.subject}
                    onSelectPreviewImage={handleSelectPreviewImage}
                    previewImagePath={previewPath}
                    saveemail={(saveFn) => {
                        saveFnRef.current = saveFn
                    }}
                 />
            )}
        </div>
    );
}

export default TemplateEmail;
